import { useMemo } from "react"
import { useNavigate } from "react-router-dom"
import { NavBarElement } from "../../../pages/platform/components/nav-bar-element.enum"
import { useAuth } from "../../../utils/context/AuthContext"
import { ExpertRole } from "../../../utils/Enums"
import { useChatNotificationsStore } from "../../../utils/stores/useChatNotificationsStore"

export interface NavItem {
    element: NavBarElement;
    title: string;
    path: string;
    description: string;
}

export const useNavBar = (currentItem: NavBarElement) => {
    const { auth } = useAuth()
    const role = auth.role
    const navigate = useNavigate()
    const { unreadSessions } = useChatNotificationsStore()

    // Memoized navigation items based on role
    const navItems = useMemo((): NavItem[] => {
        const baseItems: NavItem[] = [
            { 
                element: NavBarElement.Home, 
                title: "Home", 
                path: "/platform-experts",
                description: "Dashboard principale"
            },
            { 
                element: NavBarElement.Customers, 
                title: "Clienti", 
                path: "/platform-experts/customers",
                description: "Gestione clienti"
            },
            { 
                element: NavBarElement.Chat, 
                title: "Chat", 
                path: "/chat",
                description: "Messaggi e conversazioni"
            },
        ];

        // Add Appointments for non-admin roles
        if (role !== ExpertRole.Admin) {
            baseItems.push({
                element: NavBarElement.Appointements, 
                title: "Appuntamenti", 
                path: "/platform-experts/appointments",
                description: "Gestione appuntamenti"
            });
        }

        // Add AdminFeatures for admin role
        const roleSpecificItems: NavItem[] = role === ExpertRole.Admin 
            ? [
                { 
                    element: NavBarElement.AdminFeatures, 
                    title: "Admin", 
                    path: "#", // This will be handled by dropdown
                    description: "Funzionalità amministrative"
                },
            ]
            : [];

        const accountItem: NavItem = {
            element: NavBarElement.Account, 
            title: "Account", 
            path: "/platform-experts/account",
            description: "Impostazioni account"
        };

        return [...baseItems, ...roleSpecificItems, accountItem];
    }, [role]);

    // Navigation handlers
    const handleNavigation = (path: string) => {
        navigate(path);
    };

    const handleKeyPress = (event: React.KeyboardEvent, path: string) => {
        if (event.key === 'Enter' || event.key === ' ') {
            event.preventDefault();
            handleNavigation(path);
        }
    };

    // Check if item is active
    const isItemActive = (element: NavBarElement) => {
        if (element === NavBarElement.AdminFeatures) {
            // AdminFeatures is active when in any of the admin sections
            return currentItem === NavBarElement.Experts || 
                   currentItem === NavBarElement.Analytics || 
                   currentItem === NavBarElement.Appointements;
        }
        return currentItem === element;
    };

    // Get notification count for chat
    const getChatNotificationCount = () => unreadSessions.length;

    // Check if chat has notifications
    const hasChatNotifications = () => unreadSessions.length > 0;

    return {
        navItems,
        role,
        handleNavigation,
        handleKeyPress,
        isItemActive,
        getChatNotificationCount,
        hasChatNotifications,
    };
}; 